import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { defineMessages } from 'react-intl.macro';
import classNames from 'classnames';

import { useSelector, useDispatch } from '@tmapy/redux';

import './OrderPublishsetListItem.scss';

import { Checkbox, Link } from '@tmapy/style-guide';

import type { OrdersPublishsetEditResponse, PublishsetListResponse } from '../../openapi/types';

import { AdminOnlyIcon } from '../../publishsets/components/AdminOnlyIcon';
import { FormatTypeBadge } from '../../publishsets/components/FormatTypeBadge';
import { PriceIcon } from '../../publishsets/components/PriceIcon';

import {
  actionPublishsetAddFormat,
  actionPublishsetRemoveFormat,
  actionPublishsetAddAllFormats,
  actionPublishsetRemoveAllFormats,
} from '../actions/newOrder';

import { ApprovalIcon } from './ApprovalIcon';

const msg = defineMessages({
  publishsetListItemAllFormat: {
    id: 'publishsetListItem.allFormat',
    defaultMessage: 'Vše',
  },
  externalDescriptionUrl: {
    id: 'publishsetListItem.externalDescriptionUrl',
    defaultMessage: 'Podrobnosti',
  },
});

type PublishsetItemProps = {
  publishset: PublishsetListResponse;
};

export const OrderPublishsetListItem: React.FC<PublishsetItemProps> = ({ publishset }) => {
  const dispatch = useDispatch();
  const orderPublishsets = useSelector((state) => state.newOrder.order_publishsets);
  const hideTags = useSelector((state) => state.app.config.vydej?.hideTags);
  const customerType = useSelector((state) => state.app.config.customerType);

  const orderPublishset: OrdersPublishsetEditResponse = orderPublishsets.find(
    (orderPublishset) => orderPublishset.publishset === publishset.url,
  ) ?? {
    publishset: publishset.url,
    required_formats: [],
  };

  const publishsetUrl = orderPublishset.publishset;

  const handleCheckboxClick = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const action = e.currentTarget.checked
        ? actionPublishsetAddFormat
        : actionPublishsetRemoveFormat;
      dispatch(
        action({
          publishsetUrl,
          formatUrl: e.currentTarget.value,
        }),
      );
    },
    [dispatch, publishsetUrl],
  );

  const handleCheckboxAllClick = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.currentTarget.checked) {
        dispatch(
          actionPublishsetAddAllFormats({
            publishsetUrl,
            formatsUrl: publishset.formats.map((format) => format.url),
          }),
        );
      } else {
        dispatch(actionPublishsetRemoveAllFormats({ publishsetUrl }));
      }
    },
    [dispatch, publishsetUrl, publishset.formats],
  );
  return (
    <div
      className={classNames('dmx-publishSetItem', {
        'dmx-publishSetItem-isChecked': orderPublishset.required_formats.length > 0,
      })}
    >
      {publishset.price_list_item && <div className='dmx-publishSetItem--priceRibbon'>Kč</div>}
      <div>
        <div className='sg-a-d-f sg-a-jc-sb sg-a-ai-c'>
          <div className='sg-u-type-h2 sg-a-mr-2'>{publishset.name}</div>
          <div className='sg-a-d-f sg-a-ai-c sg-u-hsl-1'>
            {customerType === 'ipr' && publishset.admin_only && <AdminOnlyIcon />}
            {publishset.needs_approval && <ApprovalIcon />}
            {publishset.price_list_item && <PriceIcon priceListItem={publishset.price_list_item} />}
            {publishset.type && <FormatTypeBadge type={publishset.type} />}
          </div>
        </div>
        {publishset.tags && !hideTags && (
          <div className='sg-u-type-sml sg-u-type-italic sg-a-mt-1/2 sg-u-c-body-darker'>
            {publishset.tags.map((tag) => tag.name).join(', ')}
          </div>
        )}
        {publishset.description && (
          <div className='sg-a-mt-1/2 sg-u-c-body-darkest'>
            {publishset.description}
            {publishset.external_description_url && (
              <>
                {' '}
                <Link
                  severity='colorful'
                  href={publishset.external_description_url}
                  target='_blank'
                  isExternal
                >
                  <FormattedMessage {...msg.externalDescriptionUrl} />
                </Link>
              </>
            )}
          </div>
        )}
      </div>
      {publishset.formats.length > 0 && (
        <div className='sg-a-d-f sg-a-fw-w sg-u-hsr-2 sg-a-mt-1 sg-u-type-sml'>
          {publishset.formats.length > 1 && (
            <Checkbox
              name={`${publishset.id}`}
              onChange={handleCheckboxAllClick}
              isChecked={orderPublishset.required_formats.length === publishset.formats.length}
            >
              <FormattedMessage {...msg.publishsetListItemAllFormat} />
            </Checkbox>
          )}
          {publishset.formats.map((format) => (
            <Checkbox
              key={format.url}
              name={`${publishset.id}`}
              value={format.url}
              onChange={handleCheckboxClick}
              isChecked={orderPublishset.required_formats.includes(format.url)}
            >
              {format.name}
            </Checkbox>
          ))}
        </div>
      )}
    </div>
  );
};
